import * as React from 'react';
import {useEffect, useState} from 'react';
import {useAppDispatch} from '../../store';
import {isApiResultError} from '../../helpers/api';
import {OptionValue} from 'model/form';
import {SelectFormField} from 'components/form/SelectFormField';
import {createUserOption, renderUserOption} from 'components/form/UserSelectFormField';
import {fetchApprovalEligibleUsers} from 'store/approvals';
import {fetchUsers} from 'store/user';
import type {ApprovalRequestUserEligibleUsersResponse, SpaceUserIndexResponse} from '../../generated-api';

/**
 * Picks the record's user from `eligible_users`, i.e. only users the current
 * manager is allowed to create a record for. Presented like every other people
 * picker -- avatar, name, e-mail -- which takes a second request: `eligible_users`
 * answers who may be picked, but carries neither the picture nor the e-mail.
 */
export const ApprovalUserSelectField = ({name, label, placeholder, onLoaded}: {
    name: string,
    label: string,
    placeholder?: string,
    onLoaded?: (users: ApprovalRequestUserEligibleUsersResponse[]) => void,
}) => {
    const dispatch = useAppDispatch();

    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<OptionValue[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let isCurrent = true;
        setIsLoading(true);
        dispatch(fetchApprovalEligibleUsers({filterFulltext: search || undefined, perPage: 50}))
            .then(async (res) => {
                if (isApiResultError(res)) {
                    if (isCurrent) {
                        setOptions([]);
                    }
                    return;
                }
                if (!isCurrent) {
                    return;
                }
                const users = res.payload as ApprovalRequestUserEligibleUsersResponse[];
                if (onLoaded) {
                    onLoaded(users);
                }

                // asked for exactly the eligible ids, so the two lists cannot drift apart;
                // a user the list does not return simply keeps the plain name
                const detailsById = new Map<number, SpaceUserIndexResponse>();
                if (users.length) {
                    const details = await dispatch(fetchUsers({
                        filterUserIds: users.map(u => u.id).join(','),
                        paginatesPer: users.length
                    }));
                    if (!isApiResultError(details)) {
                        (details.payload as SpaceUserIndexResponse[] || []).forEach((u) => {
                            if (u.id) {
                                detailsById.set(u.id, u);
                            }
                        });
                    }
                }
                if (!isCurrent) {
                    return;
                }
                setOptions(users.map(u => createUserOption({
                    ...detailsById.get(u.id),
                    // the eligible record stays authoritative about who this is
                    id: u.id,
                    full_name: u.full_name
                })));
            })
            .finally(() => {
                if (isCurrent) {
                    setIsLoading(false);
                }
            });
        return () => {
            isCurrent = false;
        };
    }, [search, onLoaded, dispatch]);

    // shrink keeps the label above the line so it lines up with the read-only
    // user fields next to it
    return <SelectFormField name={name} label={label} placeholder={placeholder} shrink
        options={options} renderOption={renderUserOption} onSearch={setSearch} isLoading={isLoading}/>;
};
